import { ClassicChrome } from "./classic";
import type { TerminalSetup, TerminalTheme } from "./types";
import { UbuntuChrome } from "./ubuntu";

/**
 * Every look the terminal can wear, and the only place a new one is named.
 *
 * **This file plus one directory is the whole cost of a third setup.** That is
 * the claim M3/I2 makes, and `test/setup-seam.test.tsx` is what holds it to
 * account: nothing else — not `commands.ts`, not `useTerminal.ts`, not
 * `Terminal.tsx` — knows how many setups exist or what they are called.
 *
 * Themes are a separate axis. A setup names the one it looks best in, and the
 * user can override it with `theme <id>` for any setup.
 */

const midnight: TerminalTheme = {
  id: "midnight",
  title: "the original — near-black, green on slate",
  tokens: {
    "--bg": "#0b0e0f",
    "--bg-panel": "#11161a",
    "--fg": "#c8d3d5",
    "--fg-dim": "#5f7178",
    "--green": "#7fd97f",
    "--red": "#ff6b6b",
    "--amber": "#e8c07d",
    "--line": "#1c252b",
    "--chrome": "#11161a",
    "--chrome-fg": "#c8d3d5",
  },
};

const aubergine: TerminalTheme = {
  id: "aubergine",
  title: "Ubuntu — aubergine window, orange accents",
  tokens: {
    "--bg": "#300a24",
    "--bg-panel": "#3b0f2d",
    "--fg": "#eeeeec",
    "--fg-dim": "#a89aa4",
    "--green": "#4e9a06",
    "--red": "#ef2929",
    "--amber": "#e95420",
    "--line": "#5c2a4c",
    "--chrome": "#2c001e",
    "--chrome-fg": "#e8e6e3",
  },
};

const phosphor: TerminalTheme = {
  id: "phosphor",
  title: "amber CRT — one hue, warm and dim",
  tokens: {
    "--bg": "#120d05",
    "--bg-panel": "#1b1308",
    "--fg": "#ffb74d",
    "--fg-dim": "#8a6224",
    "--green": "#ffd08a",
    "--red": "#ff7043",
    "--amber": "#ffb74d",
    "--line": "#2e2110",
    "--chrome": "#1b1308",
    "--chrome-fg": "#ffb74d",
  },
};

const paper: TerminalTheme = {
  id: "paper",
  title: "light — for a bright room or a projector",
  tokens: {
    "--bg": "#fbfaf7",
    "--bg-panel": "#efece5",
    "--fg": "#25292b",
    "--fg-dim": "#6c7378",
    "--green": "#1f7a3d",
    "--red": "#b3261e",
    "--amber": "#8a5a00",
    "--line": "#dcd7cc",
    "--chrome": "#e2ded4",
    "--chrome-fg": "#25292b",
  },
};

export const THEMES: readonly TerminalTheme[] = [midnight, aubergine, phosphor, paper];

export const SETUPS: readonly TerminalSetup[] = [
  {
    id: "classic",
    title: "the original — a status bar, a ticker row, a prompt",
    defaultTheme: "midnight",
    Chrome: ClassicChrome,
  },
  {
    id: "ubuntu",
    title: "an Ubuntu terminal window, with a fixed-width status banner",
    defaultTheme: "aubergine",
    Chrome: UbuntuChrome,
  },
];

export const DEFAULT_SETUP = "classic";
export const DEFAULT_THEME = "midnight";

/** An unknown or absent id falls back rather than throwing — chrome is not worth a blank page. */
export function resolveSetup(id: string | null | undefined): TerminalSetup {
  return (
    SETUPS.find((s) => s.id === id) ??
    (SETUPS.find((s) => s.id === DEFAULT_SETUP) as TerminalSetup) ??
    (SETUPS[0] as TerminalSetup)
  );
}

export function resolveTheme(id: string | null | undefined): TerminalTheme {
  return (
    THEMES.find((t) => t.id === id) ??
    (THEMES.find((t) => t.id === DEFAULT_THEME) as TerminalTheme) ??
    (THEMES[0] as TerminalTheme)
  );
}

/** The tokens as a React `style` object. Inline, so a theme needs no stylesheet. */
export function themeStyle(theme: TerminalTheme): React.CSSProperties {
  return theme.tokens as React.CSSProperties;
}
